/**
 * Shared design vocabulary for the gift setups — vouchers AND the Goal Game
 * read these exact keys from the same saved fields (see
 * services/gift_setups.py). There is no second copy for the Goal Game.
 *
 * Fonts are SELF-HOSTED (public/fonts/design-fonts.css) and loaded ON DEMAND by
 * the screens that use them: a fan page must contact our domain only — never
 * Google Fonts or any other CDN. Nothing is added to the global CSS import, so
 * no other page pays for a font it never shows.
 */

// One stylesheet PER FAMILY, so a page downloads exactly the faces it shows.
const familyCss = (key) => `/fonts/families/${key}.css`;

/**
 * The font library. Grouped for the picker, flat for everything else.
 *
 * The first entry of each of the first four groups is one of the ORIGINAL four
 * choices — same key, same label, same stack — so every setup saved before this
 * list grew renders exactly as it did. New faces only ever append.
 *
 * `css: null` means the face is already on the page (the app's own DM Sans).
 */
export const FONT_GROUPS = [
    {
        label: 'Bold display',
        fonts: [
            { key: 'display', label: 'Bold display', family: 'Archivo Black',
              stack: "'Archivo Black', 'DM Sans', system-ui, sans-serif", css: familyCss('display') },
            { key: 'anton', label: 'Anton', family: 'Anton',
              stack: "'Anton', 'DM Sans', system-ui, sans-serif", css: familyCss('anton') },
            { key: 'alfa', label: 'Alfa Slab One', family: 'Alfa Slab One',
              stack: "'Alfa Slab One', Georgia, serif", css: familyCss('alfa') },
            { key: 'bungee', label: 'Bungee', family: 'Bungee',
              stack: "'Bungee', 'DM Sans', system-ui, sans-serif", css: familyCss('bungee') },
        ],
    },
    {
        label: 'Condensed / sports',
        fonts: [
            { key: 'condensed', label: 'Condensed sports', family: 'Oswald',
              stack: "'Oswald', 'DM Sans', system-ui, sans-serif", css: familyCss('condensed') },
            { key: 'barlow-cond', label: 'Barlow Condensed', family: 'Barlow Condensed',
              stack: "'Barlow Condensed', 'Oswald', system-ui, sans-serif", css: familyCss('barlow-cond') },
            { key: 'fjalla', label: 'Fjalla One', family: 'Fjalla One',
              stack: "'Fjalla One', 'Oswald', system-ui, sans-serif", css: familyCss('fjalla') },
            { key: 'archivo-narrow', label: 'Archivo Narrow', family: 'Archivo Narrow',
              stack: "'Archivo Narrow', 'DM Sans', system-ui, sans-serif", css: familyCss('archivo-narrow') },
        ],
    },
    {
        label: 'Clean sans',
        fonts: [
            { key: 'sans', label: 'Clean sans', family: 'DM Sans',
              stack: "'DM Sans', system-ui, sans-serif", css: null },
            { key: 'manrope', label: 'Manrope', family: 'Manrope',
              stack: "'Manrope', 'DM Sans', system-ui, sans-serif", css: familyCss('manrope') },
            { key: 'work-sans', label: 'Work Sans', family: 'Work Sans',
              stack: "'Work Sans', 'DM Sans', system-ui, sans-serif", css: familyCss('work-sans') },
            { key: 'figtree', label: 'Figtree', family: 'Figtree',
              stack: "'Figtree', 'DM Sans', system-ui, sans-serif", css: familyCss('figtree') },
            { key: 'space-grotesk', label: 'Space Grotesk', family: 'Space Grotesk',
              stack: "'Space Grotesk', 'DM Sans', system-ui, sans-serif", css: familyCss('space-grotesk') },
        ],
    },
    {
        label: 'Serif',
        fonts: [
            { key: 'serif', label: 'Serif', family: 'Playfair Display',
              stack: "'Playfair Display', Georgia, serif", css: familyCss('serif') },
            { key: 'lora', label: 'Lora', family: 'Lora',
              stack: "'Lora', Georgia, serif", css: familyCss('lora') },
            { key: 'bitter', label: 'Bitter', family: 'Bitter',
              stack: "'Bitter', Georgia, serif", css: familyCss('bitter') },
            { key: 'libre-baskerville', label: 'Libre Baskerville', family: 'Libre Baskerville',
              stack: "'Libre Baskerville', Georgia, serif", css: familyCss('libre-baskerville') },
            { key: 'abril', label: 'Abril Fatface', family: 'Abril Fatface',
              stack: "'Abril Fatface', Georgia, serif", css: familyCss('abril') },
        ],
    },
    {
        label: 'Script',
        fonts: [
            { key: 'pacifico', label: 'Pacifico', family: 'Pacifico',
              stack: "'Pacifico', cursive", css: familyCss('pacifico') },
            { key: 'caveat', label: 'Caveat', family: 'Caveat',
              stack: "'Caveat', cursive", css: familyCss('caveat') },
            { key: 'dancing', label: 'Dancing Script', family: 'Dancing Script',
              stack: "'Dancing Script', cursive", css: familyCss('dancing') },
            { key: 'great-vibes', label: 'Great Vibes', family: 'Great Vibes',
              stack: "'Great Vibes', cursive", css: familyCss('great-vibes') },
        ],
    },
    {
        label: 'Rounded / friendly',
        fonts: [
            { key: 'nunito', label: 'Nunito', family: 'Nunito',
              stack: "'Nunito', 'DM Sans', system-ui, sans-serif", css: familyCss('nunito') },
            { key: 'quicksand', label: 'Quicksand', family: 'Quicksand',
              stack: "'Quicksand', 'DM Sans', system-ui, sans-serif", css: familyCss('quicksand') },
            { key: 'comfortaa', label: 'Comfortaa', family: 'Comfortaa',
              stack: "'Comfortaa', 'DM Sans', system-ui, sans-serif", css: familyCss('comfortaa') },
            { key: 'varela-round', label: 'Varela Round', family: 'Varela Round',
              stack: "'Varela Round', 'DM Sans', system-ui, sans-serif", css: familyCss('varela-round') },
        ],
    },
];

export const FONT_CHOICES = [
    { key: '', label: 'Default (app font)', family: null, stack: null, css: null },
    ...FONT_GROUPS.flatMap((g) => g.fonts),
];

export const TEXT_SIZES = [
    { key: '', label: 'Default' },
    { key: 'sm', label: 'Small' },
    { key: 'md', label: 'Medium' },
    { key: 'lg', label: 'Large' },
    { key: 'xl', label: 'Extra large' },
];

// Readable on a phone in a dim bar: nothing below 14px for body text.
const HEADLINE_PX = { sm: 28, md: 36, lg: 46, xl: 58 };
const BODY_PX = { sm: 14, md: 16, lg: 18, xl: 20 };

const byKey = (key) => FONT_CHOICES.find((f) => f.key === (key || '')) || FONT_CHOICES[0];

/**
 * Injects the ONE stylesheet for the chosen family, once. A fan page therefore
 * downloads exactly its headline face and its body face — never the library.
 * No-op for the app's default font. Nothing outside our domain is requested.
 */
export const loadFont = (key) => {
    const font = byKey(key);
    if (!font?.css || typeof document === 'undefined') return;
    const id = `design-font-${font.key}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = font.css;
    document.head.appendChild(link);
};

export const fontStack = (key) => byKey(key).stack;

export const headlinePx = (size) => HEADLINE_PX[size] || null;
export const bodyPx = (size) => BODY_PX[size] || null;

// ── the fine size control ───────────────────────────────────────────────
// 100 means "exactly as the step renders". An unset scale returns null from the
// helpers below, so a setup with nothing saved renders byte-identically.
export const SCALE_MIN = 80;
export const SCALE_MAX = 160;
export const SCALE_DEFAULT = 100;

const factor = (scale) => {
    const n = Number(scale);
    if (!Number.isFinite(n) || n === SCALE_DEFAULT) return null;
    return Math.max(SCALE_MIN, Math.min(SCALE_MAX, n)) / 100;
};

const sized = (px, base, scale) => {
    const f = factor(scale);
    if (!px && !f) return null;                  // nothing saved → unchanged
    return `${Math.round((px || base) * (f || 1) * 10) / 10}px`;
};

/** The headline size in px, step × slider. `basePx` is the screen's own size. */
export const headlineFontSize = (size, scale, basePx = 36) =>
    sized(HEADLINE_PX[size], basePx, scale);

/** The body size in px, step × slider. */
export const bodyFontSize = (size, scale, basePx = 16) =>
    sized(BODY_PX[size], basePx, scale);

/**
 * Colour themes. The first five are the voucher presets, unchanged. Red, White
 * and Blue is new and is the GOAL GAME default only — vouchers keep theirs.
 */
export const DESIGN_PRESETS = [
    { key: 'classic', label: 'Classic', bg: '', text: '', accent: '', button: '', swatch: ['#F8FAFC', '#0F172A', '#D4AF37'] },
    { key: 'midnight', label: 'Midnight', bg: '#0B1220', text: '#E2E8F0', accent: '#3B82F6', button: '#2563EB', swatch: ['#0B1220', '#E2E8F0', '#3B82F6'] },
    { key: 'ocean', label: 'Ocean', bg: '#0C2A3A', text: '#E6F4FA', accent: '#22D3EE', button: '#0891B2', swatch: ['#0C2A3A', '#E6F4FA', '#22D3EE'] },
    { key: 'emerald', label: 'Emerald', bg: '#05231A', text: '#E7FBF3', accent: '#10B981', button: '#059669', swatch: ['#05231A', '#E7FBF3', '#10B981'] },
    { key: 'burgundy', label: 'Burgundy', bg: '#2A0A12', text: '#FBE9EE', accent: '#E11D48', button: '#BE123C', swatch: ['#2A0A12', '#FBE9EE', '#E11D48'] },
    {
        key: 'rwb', label: 'Red, White and Blue', goalGameOnly: true,
        bg: '#0A1C3F', text: '#FFFFFF', accent: '#FFFFFF', button: '#E01B2E',
        swatch: ['#0A1C3F', '#FFFFFF', '#E01B2E'],
    },
];

/** The Goal Game's fallback look when nothing is saved: Red, White and Blue. */
export const GOAL_GAME_DEFAULT = DESIGN_PRESETS.find((p) => p.key === 'rwb');
